/**
 * Public product page used by prospective clients and integration reviewers.
 * It stays outside the login gate so the product, data use, and user controls
 * are clear before anyone grants access to an advertising account.
 */

export const PUBLIC_HOME_PATH = "/";

export default function PublicHomePage(): React.ReactElement {
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <a
          href={PUBLIC_HOME_PATH}
          aria-label="Scale Advisors Data Dashboard home"
          style={styles.brand}
        >
          <span aria-hidden style={styles.mark}>
            SA
          </span>
          <span>Scale Advisors Data Dashboard</span>
        </a>
        <nav aria-label="Public pages" style={styles.nav}>
          <a href="/privacy" style={styles.navLink}>
            Privacy
          </a>
          <a href="/terms" style={styles.navLink}>
            Terms
          </a>
          <a href="/data-deletion" style={styles.navLink}>
            Data deletion
          </a>
        </nav>
      </header>

      <main style={styles.main}>
        <section style={styles.hero}>
          <div style={styles.heroCopy}>
            <p style={styles.eyebrow}>Profit reporting for paid ads</p>
            <h1 style={styles.title}>See what your ads leave behind.</h1>
            <p style={styles.lead}>
              Bring daily ad spend by ad account and campaign into the same profit view as your
              sales and costs.
            </p>
            <div style={styles.actions}>
              <a href="/demo" style={styles.primaryButton}>
                View the demo
              </a>
              <a href="/login" style={styles.secondaryButton}>
                Sign in
              </a>
            </div>
          </div>

          <aside aria-labelledby="connections-title" style={styles.connectionPanel}>
            <h2 id="connections-title" style={styles.panelTitle}>
              What the dashboard connects
            </h2>
            <p style={styles.panelLead}>
              Scale Advisors Data Dashboard connects Google Ads and Microsoft Ads to import daily ad
              spend by ad account and campaign for profit reporting.
            </p>
            <div style={styles.providerList}>
              <div style={styles.providerRow}>
                <strong style={styles.providerName}>Google Ads</strong>
                <span style={styles.providerCopy}>Daily ad spend by ad account and campaign</span>
              </div>
              <div style={styles.providerRow}>
                <strong style={styles.providerName}>Microsoft Ads</strong>
                <span style={styles.providerCopy}>Daily ad spend by ad account and campaign</span>
              </div>
            </div>
            <p style={styles.readOnlyNote}>
              These connections are used for reporting. The dashboard does not create or edit ads.
            </p>
          </aside>
        </section>

        <section style={styles.controlSection}>
          <div style={styles.controlCopy}>
            <h2 style={styles.sectionTitle}>You stay in control.</h2>
            <p style={styles.sectionBody}>
              You sign in with Google or Microsoft on their own page. You choose the ad account to
              connect.
            </p>
          </div>
          <div style={styles.controlDetails}>
            <h3 style={styles.detailTitle}>Disconnect any time</h3>
            <p style={styles.detailBody}>
              You can disconnect an ad account from the Connections page. This stops all new imports
              from that account.
            </p>
            <a href="/privacy" style={styles.textLink}>
              Read how we handle your data
            </a>
          </div>
        </section>
      </main>

      <footer style={styles.footer}>
        <span>Scale Advisors LLC</span>
        <nav aria-label="Legal and support" style={styles.footerLinks}>
          <a href="/privacy" style={styles.footerLink}>
            Privacy
          </a>
          <a href="/terms" style={styles.footerLink}>
            Terms
          </a>
          <a href="/data-deletion" style={styles.footerLink}>
            Data deletion
          </a>
          <a href="mailto:help@scaleadvisors.com" style={styles.footerLink}>
            Contact
          </a>
        </nav>
      </footer>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100dvh",
    background: "var(--bg)",
    color: "var(--text)",
  },
  header: {
    width: "min(1180px, calc(100% - 32px))",
    minHeight: 68,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px 24px",
    padding: "14px 0",
    borderBottom: "1px solid var(--border)",
  },
  brand: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    color: "var(--text)",
    fontFamily: "var(--font-display)",
    fontSize: 15,
    fontWeight: 700,
    textDecoration: "none",
  },
  mark: {
    display: "inline-grid",
    width: 32,
    height: 32,
    placeItems: "center",
    borderRadius: "var(--r-sm)",
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    fontSize: 11,
    letterSpacing: "0.04em",
  },
  nav: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px 18px",
  },
  navLink: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    color: "var(--body)",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "none",
  },
  main: {
    width: "min(1180px, calc(100% - 32px))",
    margin: "0 auto",
  },
  hero: {
    minHeight: "min(660px, calc(100dvh - 69px))",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))",
    alignItems: "center",
    gap: "clamp(36px, 7vw, 96px)",
    padding: "clamp(52px, 9vw, 96px) 0",
  },
  heroCopy: {
    maxWidth: 610,
  },
  eyebrow: {
    margin: "0 0 16px",
    color: "var(--accent)",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
  },
  title: {
    maxWidth: 600,
    margin: 0,
    fontSize: "clamp(42px, 7vw, 68px)",
    lineHeight: 0.98,
    letterSpacing: "-0.045em",
  },
  lead: {
    maxWidth: 560,
    margin: "24px 0 0",
    color: "var(--body)",
    fontSize: "clamp(17px, 2.2vw, 20px)",
    lineHeight: 1.55,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 30,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    padding: "0 18px",
    borderRadius: "var(--r-sm)",
    background: "var(--accent)",
    color: "var(--accent-contrast)",
    fontWeight: 800,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 44,
    padding: "0 18px",
    border: "1px solid var(--border)",
    borderRadius: "var(--r-sm)",
    background: "var(--surface)",
    color: "var(--text)",
    fontWeight: 750,
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  connectionPanel: {
    maxWidth: 500,
    justifySelf: "end",
    border: "1px solid var(--border)",
    borderRadius: "var(--r-card)",
    background: "var(--surface)",
    boxShadow: "var(--shadow-card)",
    padding: "clamp(24px, 4vw, 36px)",
  },
  panelTitle: {
    margin: 0,
    fontSize: 22,
  },
  panelLead: {
    margin: "12px 0 0",
    color: "var(--body)",
    fontSize: 16,
    lineHeight: 1.55,
  },
  providerList: {
    marginTop: 26,
    borderTop: "1px solid var(--border)",
  },
  providerRow: {
    display: "grid",
    gridTemplateColumns: "minmax(120px, 0.8fr) minmax(0, 1.2fr)",
    gap: 16,
    padding: "17px 0",
    borderBottom: "1px solid var(--border)",
  },
  providerName: {
    fontSize: 14,
  },
  providerCopy: {
    color: "var(--body)",
    fontSize: 14,
    lineHeight: 1.4,
  },
  readOnlyNote: {
    margin: "22px 0 0",
    color: "var(--body)",
    fontSize: 13,
    lineHeight: 1.5,
  },
  controlSection: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
    gap: "clamp(28px, 7vw, 88px)",
    padding: "clamp(54px, 8vw, 84px) 0",
    borderTop: "1px solid var(--border)",
  },
  controlCopy: {
    maxWidth: 520,
  },
  sectionTitle: {
    margin: 0,
    fontSize: "clamp(28px, 4vw, 42px)",
    lineHeight: 1.08,
    letterSpacing: "-0.025em",
  },
  sectionBody: {
    margin: "16px 0 0",
    color: "var(--body)",
    fontSize: 17,
    lineHeight: 1.6,
  },
  controlDetails: {
    maxWidth: 520,
    padding: "4px 0 0",
  },
  detailTitle: {
    margin: 0,
    fontSize: 18,
  },
  detailBody: {
    margin: "10px 0 14px",
    color: "var(--body)",
    fontSize: 15,
    lineHeight: 1.6,
  },
  textLink: {
    color: "var(--accent)",
    fontSize: 14,
    fontWeight: 750,
    textDecorationThickness: "1px",
    textUnderlineOffset: 3,
  },
  footer: {
    width: "min(1180px, calc(100% - 32px))",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px 24px",
    padding: "24px 0 32px",
    borderTop: "1px solid var(--border)",
    color: "var(--body)",
    fontSize: 13,
  },
  footerLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 18px",
  },
  footerLink: {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 44,
    color: "var(--body)",
    textDecoration: "none",
  },
};
